// Effective access trace — answers "what can <alias> do?" (mockups/access-
// preview-v6.html "trace" view). Three-column flow + permission table.
//
// There is no /api/admin/access/trace endpoint; we derive the access chain
// client-side by composing the per-user grants envelope with the rule and
// role catalogs. We pick the first group whose name matches a rule's
// `group_match.group_name` as the "primary" chain — that's the rule which
// granted the operator the surfaced role + scope. If no matching rule is
// found we render a "no access yet" card explaining why.

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ApiError, api, useApiErrorHandler } from "@/api";
import { useToast } from "@/components";
import { ruleClient } from "@/data/clients/ruleClient";
import { roleClient } from "@/data/clients/roleClient";
import { permissionClient } from "@/data/clients/permissionClient";
import { locationsClient } from "@/api/locationsClient";
import {
  permissionDisplayName,
  type PermissionCatalog,
  type PermissionEntry,
  type Role,
} from "@/types/role";
import type { Rule, LocationScope } from "@/types/rule";
import type { Location } from "@/types/location";
import { relativeTime, roleClass, scopeSummary } from "@/pages/admin/people/format";
import "../AdminShared.css";

// ---------------------------------------------------------------------------
// Wire shapes — tolerant. The BFF surfaces grants under either an "items"
// envelope or the legacy "grants" key, depending on snapshot.
// ---------------------------------------------------------------------------

interface ApiGrant {
  alias: string;
  ring_user_id?: string;
  display_name?: string;
  group_names?: string[];
  last_seen_at?: string;
  /** When the BFF flattens the user record onto the grants envelope. */
  user?: {
    alias?: string;
    ring_user_id?: string;
    display_name?: string;
    group_names?: string[];
    last_seen_at?: string;
  };
}

interface GrantsResponse {
  alias?: string;
  ring_user_id?: string;
  display_name?: string;
  group_names?: string[];
  last_seen_at?: string;
  items?: ApiGrant[];
  grants?: ApiGrant[];
}

interface ApiUserSummary {
  alias: string;
  ring_user_id?: string;
  display_name?: string;
  group_count?: number;
  group_names?: string[];
  last_seen_at?: string;
}

interface ApiGroup {
  group_name: string;
  group_id?: string;
  display_name?: string;
  member_count?: number;
}

interface GroupsResponse {
  items?: ApiGroup[];
  groups?: ApiGroup[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function unwrap<T>(
  resp: { items?: T[] } | undefined,
  legacyKey: string,
): T[] {
  if (!resp) return [];
  if (Array.isArray(resp.items)) return resp.items;
  const r = resp as Record<string, unknown>;
  const legacy = r[legacyKey];
  return Array.isArray(legacy) ? (legacy as T[]) : [];
}

function initial(s: string): string {
  return (s || "?").charAt(0).toUpperCase();
}

interface AccessChain {
  group: ApiGroup;
  rule: Rule;
  role: Role | null;
}

/**
 * Returns every group→rule→role chain bound to the user. A user can be in
 * multiple groups, each pointing at different rules/roles; trace must
 * reflect the union, not just the first match.
 *
 * Multiple rules can also bind the same group; we keep all of them so the
 * trace reflects the actual rule set rather than collapsing to one row.
 */
function pickAllChains(
  groupNames: string[],
  groups: Map<string, ApiGroup>,
  rules: Rule[],
  roles: Map<string, Role>,
): AccessChain[] {
  const out: AccessChain[] = [];
  const seen = new Set<string>(); // dedupe by rule_id (a rule maps to one role)
  for (const gn of groupNames) {
    const matchingRules = rules.filter(
      (r) => r.enabled && r.group_match?.group_name === gn,
    );
    if (matchingRules.length === 0) continue;
    const group: ApiGroup = groups.get(gn) ?? { group_name: gn };
    for (const rule of matchingRules) {
      if (seen.has(rule.rule_id)) continue;
      seen.add(rule.rule_id);
      out.push({
        group,
        rule,
        role: roles.get(rule.role_id) ?? null,
      });
    }
  }
  return out;
}

/** Build a friendly "All N locations" / "<n> locations" headline + sub. */
function locationHeadline(
  scope: LocationScope,
  totalLocations: number,
  locationName: (id: string) => string | undefined,
): { headline: string; sub: string } {
  if (scope.mode === "ALL") {
    return {
      headline:
        totalLocations > 0
          ? `All ${totalLocations} location${totalLocations === 1 ? "" : "s"}`
          : "All locations",
      sub: "",
    };
  }
  const ids = scope.ids;
  if (ids.length === 0) {
    return {
      headline: scope.mode === "DENY" ? "No locations" : "No locations",
      sub: "",
    };
  }
  const names = ids.map((id) => locationName(id) ?? id);
  return {
    headline: `${ids.length} location${ids.length === 1 ? "" : "s"}`,
    sub: names.join(", "),
  };
}

/** Flatten the catalog into a key→entry lookup. */
function buildPermissionLookup(
  catalog: PermissionCatalog | null,
): Map<string, PermissionEntry> {
  const map = new Map<string, PermissionEntry>();
  if (!catalog) return map;
  for (const g of catalog.groups) {
    for (const p of g.permissions) {
      map.set(p.key, p);
    }
  }
  return map;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function TracePage(): JSX.Element {
  const [params] = useSearchParams();
  const aliasParam = params.get("alias")?.trim() ?? "";

  if (aliasParam.length === 0) {
    return <AliasPickerCard heading="Trace effective access" />;
  }

  return <TraceBody alias={aliasParam} />;
}

// ---------------------------------------------------------------------------
// Alias picker (centered card shown when ?alias is missing/empty)
// ---------------------------------------------------------------------------

interface AliasPickerCardProps {
  heading: string;
  message?: string;
}

function AliasPickerCard({ heading, message }: AliasPickerCardProps): JSX.Element {
  const navigate = useNavigate();
  const [value, setValue] = useState("");

  const onSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const trimmed = value.trim();
    if (trimmed.length === 0) return;
    navigate(`/admin/trace?alias=${encodeURIComponent(trimmed)}`);
  };

  return (
    <section className="admin-page">
      <div className="v6-trace-frame" style={{ maxWidth: 480, margin: "48px auto" }}>
        <h2 style={{ marginTop: 0, fontSize: 20, fontWeight: 500 }}>{heading}</h2>
        {message ? (
          <p style={{ color: "var(--ring-neutral-300)", fontSize: 13 }}>{message}</p>
        ) : (
          <p style={{ color: "var(--ring-neutral-300)", fontSize: 13 }}>
            Enter an alias to see the rules, role, and locations that compose
            their access.
          </p>
        )}
        <form
          onSubmit={onSubmit}
          style={{ display: "flex", gap: 8, marginTop: 16 }}
        >
          <input
            type="text"
            placeholder="alias"
            value={value}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
            aria-label="Alias"
            autoFocus
            style={{
              flex: 1,
              padding: "8px 12px",
              border: "1px solid var(--ring-tertiary-border)",
              borderRadius: 6,
              fontSize: 14,
            }}
          />
          <button type="submit" className="v6-btn primary">
            Show access
          </button>
        </form>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Trace body — alias is non-empty
// ---------------------------------------------------------------------------

interface TraceBodyProps {
  alias: string;
}

interface TraceData {
  grant: ApiGrant | null;
  ringUserId: string | null;
  groupNames: string[];
  lastSeenAt: string | null;
  groups: ApiGroup[];
  rules: Rule[];
  roles: Role[];
  locations: Location[];
  catalog: PermissionCatalog | null;
}

function TraceBody({ alias }: TraceBodyProps): JSX.Element {
  const handleApiError = useApiErrorHandler();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [data, setData] = useState<TraceData | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const grantsPath = `/api/users/${encodeURIComponent(alias)}/grants`;
      const [
        grantsResp,
        usersResp,
        groupsResp,
        rulesResp,
        rolesResp,
        catalog,
        locations,
      ] = await Promise.all([
        api.get<GrantsResponse>(grantsPath).catch((err: unknown) => {
          if (err instanceof ApiError && err.status === 404) {
            return null;
          }
          throw err;
        }),
        // /api/users carries group_names + ring_user_id even when the
        // grants envelope is empty. We use it as the source of truth for
        // identity metadata; grants only fills in scoped grants. The
        // tolerant unwrap handles either {items} or {users} shapes.
        api
          .get<{ items?: ApiUserSummary[]; users?: ApiUserSummary[] }>(
            "/api/users",
          )
          .catch((err: unknown) => {
            if (err instanceof ApiError && err.status === 404) {
              return { items: [] };
            }
            throw err;
          }),
        api.get<GroupsResponse>("/api/groups").catch((err: unknown) => {
          if (err instanceof ApiError && err.status === 404) {
            return { items: [] } as GroupsResponse;
          }
          throw err;
        }),
        ruleClient.list({ limit: 200 }),
        roleClient.list({ limit: 200 }),
        permissionClient.getCatalog().catch(() => null),
        locationsClient.list().catch(() => [] as Location[]),
      ]);

      const userRecord = unwrap<ApiUserSummary>(usersResp, "users").find(
        (u) => u.alias === alias,
      );

      if (grantsResp === null && !userRecord) {
        setNotFound(true);
        setData({
          grant: null,
          ringUserId: null,
          groupNames: [],
          lastSeenAt: null,
          groups: unwrap<ApiGroup>(groupsResp, "groups"),
          rules: rulesResp.items,
          roles: rolesResp.items,
          locations,
          catalog,
        });
        return;
      }

      const items = grantsResp ? unwrap<ApiGrant>(grantsResp, "grants") : [];
      const first = items[0] ?? null;
      const inner = first?.user;
      const ringUserId =
        first?.ring_user_id ??
        inner?.ring_user_id ??
        grantsResp?.ring_user_id ??
        userRecord?.ring_user_id ??
        null;
      const groupNames =
        (first?.group_names ?? inner?.group_names ?? grantsResp?.group_names)
          ?? userRecord?.group_names
          ?? [];
      const lastSeenAt =
        first?.last_seen_at ??
        inner?.last_seen_at ??
        grantsResp?.last_seen_at ??
        userRecord?.last_seen_at ??
        null;

      setData({
        grant: first,
        ringUserId,
        groupNames,
        lastSeenAt,
        groups: unwrap<ApiGroup>(groupsResp, "groups"),
        rules: rulesResp.items,
        roles: rolesResp.items,
        locations,
        catalog,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        handleApiError(err, { actionLabel: "Trace access" });
      }
    } finally {
      setLoading(false);
    }
  }, [alias, handleApiError]);

  useEffect(() => {
    void load();
  }, [load]);

  const onForceLogout = useCallback(async () => {
    try {
      await api.delete(
        `/api/admin/sessions?alias=${encodeURIComponent(alias)}`,
      );
      toast.success({
        title: "Forced log out",
        description: `${alias} will be signed out on their next request.`,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        handleApiError(err, { actionLabel: "Force log out" });
      }
    }
  }, [alias, handleApiError, toast]);

  // -------------------------------------------------------------------------
  // Cross-reference & derive
  // -------------------------------------------------------------------------

  const groupMap = useMemo<Map<string, ApiGroup>>(() => {
    const map = new Map<string, ApiGroup>();
    if (!data) return map;
    for (const g of data.groups) map.set(g.group_name, g);
    return map;
  }, [data]);

  const roleMap = useMemo<Map<string, Role>>(() => {
    const map = new Map<string, Role>();
    if (!data) return map;
    for (const r of data.roles) map.set(r.role_id, r);
    return map;
  }, [data]);

  const locationMap = useMemo<Map<string, string>>(() => {
    const map = new Map<string, string>();
    if (!data) return map;
    for (const l of data.locations) map.set(l.ring_location_id, l.display_name);
    return map;
  }, [data]);

  const chains = useMemo<AccessChain[]>(() => {
    if (!data) return [];
    return pickAllChains(data.groupNames, groupMap, data.rules, roleMap);
  }, [data, groupMap, roleMap]);
  const chain = chains[0] ?? null;

  const permissionLookup = useMemo(
    () => buildPermissionLookup(data?.catalog ?? null),
    [data],
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (loading) {
    return (
      <section className="admin-page">
        <header className="v6-page-header">
          <div className="breadcrumbs">
            <Link to="/admin/people">{"←"} Groups & users</Link>
          </div>
          <div className="row">
            <h1>
              What can <code>{alias}</code> do?
            </h1>
          </div>
          <div className="caption">Loading effective access…</div>
        </header>
      </section>
    );
  }

  // No grants envelope at all → user record itself isn't known.
  if (notFound) {
    return (
      <AliasPickerCard
        heading={`No user found for "${alias}"`}
        message="That alias doesn't have a record in this workspace yet. Try another alias."
      />
    );
  }

  // We have a user envelope but no rule binds any of their groups → no access.
  if (!chain) {
    return (
      <section className="admin-page">
        <header className="v6-page-header">
          <div className="breadcrumbs">
            <Link to="/admin/people">{"←"} Groups & users</Link>
          </div>
          <div className="row">
            <h1>
              What can <code>{alias}</code> do?
            </h1>
          </div>
          <div className="caption">
            Effective access — composed from group memberships, rules, and
            deny-wins resolution.
          </div>
        </header>
        <div className="v6-trace-frame">
          <div className="v6-trace-subject">
            <div className="avatar-lg">{initial(alias)}</div>
            <div>
              <h2>{alias}</h2>
              <div className="meta">
                {data?.ringUserId ? (
                  <>Ring user <code>{data.ringUserId}</code> · </>
                ) : null}
                {data?.groupNames.length ?? 0} group
                {(data?.groupNames.length ?? 0) === 1 ? "" : "s"} · last seen{" "}
                {relativeTime(data?.lastSeenAt)}
              </div>
            </div>
          </div>
          <p style={{ color: "var(--ring-neutral-300)", fontSize: 14 }}>
            <b>{alias}</b> has no access yet — none of their groups are bound
            to a rule. Add a rule for one of their groups (
            {data && data.groupNames.length > 0
              ? data.groupNames.join(", ")
              : "—"}
            ) to grant access.
          </p>
        </div>
      </section>
    );
  }

  const totalLocations = data?.locations.length ?? 0;

  return (
    <section className="admin-page">
      <header className="v6-page-header">
        <div className="breadcrumbs">
          <Link to="/admin/people">{"←"} Groups & users</Link>
        </div>
        <div className="row">
          <h1>
            What can <code>{alias}</code> do?
          </h1>
        </div>
        <div className="caption">
          Effective access — composed from group memberships, rules, and
          deny-wins resolution.
        </div>
      </header>

      <div className="v6-trace-frame">
        <div className="v6-trace-subject">
          <div className="avatar-lg">{initial(alias)}</div>
          <div>
            <h2>{alias}</h2>
            <div className="meta">
              {data?.ringUserId ? (
                <>
                  Ring user <code>{data.ringUserId}</code> ·{" "}
                </>
              ) : null}
              {data?.groupNames.length ?? 0} group
              {(data?.groupNames.length ?? 0) === 1 ? "" : "s"} · last seen{" "}
              {relativeTime(data?.lastSeenAt)} ·{" "}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  void onForceLogout();
                }}
                style={{ color: "var(--ring-primary-500)", textDecoration: "none" }}
              >
                Force log out
              </a>
            </div>
          </div>
        </div>

        {chains.map((c, idx) => {
          const cGroup = c.group;
          const cRule = c.rule;
          const cRole = c.role;
          const cLoc = locationHeadline(cRule.location_scope, totalLocations, (id) =>
            locationMap.get(id),
          );
          const cScopeText = scopeSummary(
            cRule.location_scope,
            totalLocations,
            (id) => locationMap.get(id),
          );
          const cHeld = cRole?.permission_set ?? [];
          const cGroupDisplay = cGroup.display_name ?? cGroup.group_name;
          const cRoleDisplay = cRole?.display_name ?? cRule.role_id;
          return (
            <div key={cRule.rule_id}>
              {idx > 0 && (
                <div className="v6-section-head" style={{ marginTop: 24 }}>
                  And also
                </div>
              )}
              <div className="v6-trace-flow">
                <div className="v6-trace-col">
                  <div className="col-label">They belong to</div>
                  <div className="col-card">
                    <div className="name">{cGroupDisplay}</div>
                    <div className="sub">
                      {cGroup.member_count !== undefined ? (
                        <>
                          {cGroup.member_count} member
                          {cGroup.member_count === 1 ? "" : "s"} ·{" "}
                        </>
                      ) : null}
                      <code style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>
                        {cGroup.group_name}
                      </code>
                    </div>
                  </div>
                </div>
                <div className="v6-trace-col">
                  <div className="col-label">Which gets the role</div>
                  <div className="col-card">
                    <div className="name">
                      <span className={`v6-role-chip ${roleClass(cRule.role_id)}`}>
                        {cRule.role_id.toUpperCase()}
                      </span>
                      {cRoleDisplay}
                    </div>
                    <div className="sub">
                      via rule{" "}
                      <code style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>
                        {cRule.rule_id}
                      </code>
                    </div>
                  </div>
                </div>
                <div className="v6-trace-col">
                  <div className="col-label">At these locations</div>
                  <div className="col-card">
                    <div className="name">{cLoc.headline}</div>
                    {cLoc.sub.length > 0 ? <div className="sub">{cLoc.sub}</div> : null}
                  </div>
                </div>
              </div>

              <div className="v6-section-head">What that lets them do</div>

              <table className="v6-perm-table">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>Held</th>
                    <th>Permission</th>
                    <th style={{ width: 200 }}>Scope</th>
                  </tr>
                </thead>
                <tbody>
                  {cHeld.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="admin-muted" style={{ padding: 16 }}>
                        This role has no permissions assigned.
                      </td>
                    </tr>
                  ) : (
                    cHeld.map((key) => {
                      const entry = permissionLookup.get(key);
                      const name = entry ? permissionDisplayName(entry) : key;
                      const description = entry?.description ?? "";
                      return (
                        <tr key={key}>
                          <td className="held">
                            <span className="check">{"✓"}</span>
                          </td>
                          <td>
                            <b>{name}</b>
                            <div className="perm-desc">
                              {key}
                              {description.length > 0 ? ` — ${description}` : ""}
                            </div>
                          </td>
                          <td>{cLoc.headline}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>

              <div className="v6-trace-why">
                <b>Why this access?</b> {alias} is a member of <b>{cGroupDisplay}</b>.
                The rule <code>{cRule.rule_id}</code> grants that group the{" "}
                <b>{cRule.role_id.toUpperCase()}</b> role with{" "}
                {cRule.location_scope.mode === "ALL"
                  ? "no scope restriction"
                  : `scope ${cScopeText}`}
                , so they get every permission in the role at every location.
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
